import * as THREE from 'three';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

/** Half-resolution GTAO over the existing MSAA image. No second tone-mapping pass. */
export class ContactShadows {
  private readonly ao: GTAOPass;
  private frame = new THREE.FramebufferTexture(1, 1);
  private readonly material: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;
  private size = new THREE.Vector2();
  frames = 0;
  lastPasses = { aoCalls: 0, aoTriangles: 0, colorCalls: 0, colorTriangles: 0, compositeCalls: 0, compositeTriangles: 0 };

  constructor(private renderer: THREE.WebGLRenderer, private scene: THREE.Scene, private camera: THREE.Camera) {
    this.ao = new GTAOPass(scene, camera, 1, 1);
    this.ao.output = GTAOPass.OUTPUT.Off;
    this.ao.updateGtaoMaterial({ radius: .35, thickness: .7, distanceFallOff: 1, scale: 1, samples: 16 });
    this.ao.updatePdMaterial({ radius: 5, samples: 16, depthPhi: 3, normalPhi: 4 });
    this.material = new THREE.ShaderMaterial({
      uniforms: { colorImage: { value: this.frame }, ambientOcclusion: { value: this.ao.gtaoMap }, intensity: { value: .6 } },
      depthTest: false, depthWrite: false, toneMapped: false,
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
      fragmentShader: `uniform sampler2D colorImage; uniform sampler2D ambientOcclusion; uniform float intensity; varying vec2 vUv;
        void main() {
          // Copy RGBA8 bytes without a framebuffer format conversion, then decode once.
          vec4 base = sRGBTransferEOTF(texture2D(colorImage, vUv));
          float ao = texture2D(ambientOcclusion, vUv).r;
          gl_FragColor = vec4(base.rgb * mix(1., ao, intensity), base.a);
          #include <colorspace_fragment>
        }`,
    });
    this.quad = new FullScreenQuad(this.material);
  }

  setIntensity(value: number): void { this.material.uniforms.intensity.value = THREE.MathUtils.clamp(value, 0, 1); }

  render(): void {
    const renderer = this.renderer;
    const count = () => ({ calls: renderer.info?.render.calls ?? 0, triangles: renderer.info?.render.triangles ?? 0 });
    const before = count();
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    if (!size.equals(this.size)) {
      this.size.copy(size); this.frame.dispose();
      this.frame = new THREE.FramebufferTexture(size.x, size.y);
      this.frame.colorSpace = THREE.NoColorSpace; // RGBA8 must match the canvas copy format.
      this.material.uniforms.colorImage.value = this.frame;
      this.ao.setSize(Math.max(1, Math.ceil(size.x / 2)), Math.max(1, Math.ceil(size.y / 2)));
    }
    const hidden: THREE.Object3D[] = [];
    this.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      const materials = mesh.isMesh ? (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) : [];
      if (object.visible && (object.userData.kind === 'view' || object.userData.noContactShadows
        || object.userData.selectionPresentation || object.userData.effectPresentation
        || (materials.length > 0 && materials.every(material => material.transparent && material.opacity < .5)))) {
        hidden.push(object); object.visible = false;
      }
    });
    const originalTarget = renderer.getRenderTarget();
    const shadowUpdate = renderer.shadowMap.autoUpdate;
    const shadowNeedsUpdate = renderer.shadowMap.needsUpdate;
    const override = this.scene.overrideMaterial;
    try {
      renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = false;
      this.ao.render(renderer, this.ao.gtaoRenderTarget, this.ao.gtaoRenderTarget, 0, false);
    } finally {
      for (const object of hidden) object.visible = true;
      this.scene.overrideMaterial = override;
      renderer.shadowMap.autoUpdate = shadowUpdate; renderer.shadowMap.needsUpdate = shadowNeedsUpdate;
      renderer.setRenderTarget(originalTarget);
    }
    // Keep the direct renderer's exact ACES, unlit window photos, and hardware MSAA.
    const afterAO = count();
    renderer.render(this.scene, this.camera);
    const afterColor = count();
    renderer.copyFramebufferToTexture(this.frame);
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    try { this.quad.render(renderer); } finally { renderer.autoClear = autoClear; }
    const after = count();
    this.lastPasses = { aoCalls: afterAO.calls - before.calls, aoTriangles: afterAO.triangles - before.triangles,
      colorCalls: afterColor.calls - afterAO.calls, colorTriangles: afterColor.triangles - afterAO.triangles,
      compositeCalls: after.calls - afterColor.calls, compositeTriangles: after.triangles - afterColor.triangles };
    this.frames++;
  }

  dispose(): void {
    this.ao.dispose();
    // r185 GTAOPass.dispose does not release these two shader materials.
    this.ao.gtaoMaterial.dispose(); this.ao.blendMaterial.dispose();
    this.frame.dispose(); this.material.dispose(); this.quad.dispose();
  }
}
